import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { api, type ApiError } from '@/lib/api';
import { qk } from '@/lib/query-keys';
import type { AggregatedDiff, PromotionTarget } from '@/types/api';

/**
 * The aggregated multi-repo diff.
 *
 * `pre-staging` against the PROMOTION TARGET per repo — not a chat-relative diff
 * — so what is on screen is exactly what the promotion buttons would merge. That
 * is why the target selector re-fetches rather than re-filtering: a staging diff
 * and a production diff are different questions with different answers, and
 * after a staging merge the production answer changes underneath.
 *
 * `repo_id` narrows to one repo AND lifts its truncation budget, which is the
 * documented escape from an aggregate that was cut.
 *
 * Not polled. The diff only moves when a run pushes or a promotion merges, and
 * both of those invalidate this key through the graph in lib/query-keys.ts. A
 * timer here would re-download megabytes of hunks to learn nothing.
 */
export function useAggregatedDiff(
  chatId: number,
  target: PromotionTarget,
  repoId: number | null,
  enabled = true,
): UseQueryResult<AggregatedDiff, ApiError> {
  return useQuery({
    queryKey: qk.diff(chatId, target, repoId),
    queryFn: () =>
      api.get<AggregatedDiff>(`/expert/chats/${chatId}/diff`, {
        target,
        repo_id: repoId ?? undefined,
        context_lines: 3,
      }),
    enabled,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    // A diff is expensive to compute and expensive to ship. Keeping the previous
    // answer on screen while a target switch resolves beats blanking the pane.
    placeholderData: (previous) => previous,
  });
}
